import './style/global.less';
import React, { useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { createStore } from 'redux';
import { Provider, useDispatch } from 'react-redux';
import { QueryClientProvider } from '@tanstack/react-query';
import { ConfigProvider } from '@arco-design/web-react';
import zhCN from '@arco-design/web-react/es/locale/zh-CN';
import enUS from '@arco-design/web-react/es/locale/en-US';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { useLanguageStore } from '@/store/useLanguageStore';
import rootReducer from './store';
import AppLayout from './layout';
import Login from './pages/login';
import AuthCallback from './pages/auth/callback';
import Forbidden from './pages/exception/forbidden';
import checkLogin from './utils/checkLogin';
import { hasAuthMarkerCookie } from './utils/authSession';
import { queryClient } from './lib/query/queryClient';

const store = createStore(rootReducer);

function Bootstrap({ children }: { children: React.ReactNode }) {
  const dispatch = useDispatch();

  useEffect(() => {
    if (hasAuthMarkerCookie()) {
      void checkLogin(dispatch);
    } else {
      dispatch({ type: 'set-hydrated', payload: true });
    }
  }, [dispatch]);

  return <>{children}</>;
}

function App() {
  const lang = useLanguageStore((s) => s.lang);
  const arcoLocale = lang === 'en' ? enUS : zhCN;

  return (
    <Provider store={store}>
      <QueryClientProvider client={queryClient}>
        <ConfigProvider locale={arcoLocale}>
          <BrowserRouter>
            <Bootstrap>
              <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/auth/callback" element={<AuthCallback />} />
                <Route path="/forbidden" element={<Forbidden />} />
                <Route path="/*" element={<AppLayout />} />
              </Routes>
            </Bootstrap>
          </BrowserRouter>
        </ConfigProvider>
      </QueryClientProvider>
    </Provider>
  );
}

const container = document.getElementById('root');
if (container) {
  createRoot(container).render(
    <React.StrictMode>
      <App />
    </React.StrictMode>,
  );
}
